import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import {
  ArrowRight,
  Key,
  MultiplePages,
  ProfileCircle,
  Search,
  ViewGrid,
} from "iconoir-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { IconButton } from "@/components/ui/icon-button";
import { Icon } from "@/design-system/icons/icon";
import { m } from "@/i18n/en";
import { cn } from "@/lib/cn";

const destinations = [
  {
    path: "/personal-info",
    title: m.navigation.personal,
    keywords: "name photo account id profile",
    icon: ProfileCircle,
  },
  {
    path: "/security",
    title: m.navigation.security,
    keywords: "password sign in",
    icon: Key,
  },
  {
    path: "/sessions",
    title: m.navigation.sessions,
    keywords: "devices active sign out",
    icon: MultiplePages,
  },
  {
    path: "/products",
    title: m.navigation.products,
    keywords: "scholight scholens connected",
    icon: ViewGrid,
  },
] as const;

export function SearchPalette({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return destinations;
    return destinations.filter((item) =>
      `${item.title} ${item.keywords}`.toLowerCase().includes(normalized),
    );
  }, [query]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const choose = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <>
      <button
        className={cn(
          "hidden h-[var(--control-search-height)] w-[398px] items-center gap-3 rounded-[var(--radius-md)] bg-subtle px-4 text-left text-sm text-secondary hover:bg-hover lg:flex",
          className,
        )}
        onClick={() => setOpen(true)}
        type="button"
      >
        <span className="grid size-5 place-items-center">
          <Icon glyph={Search} />
        </span>
        <span className="min-w-0 flex-1">{m.search.placeholder}</span>
        <kbd className="text-xs font-medium text-secondary">⌘ K</kbd>
      </button>
      <IconButton
        className="lg:hidden"
        label={m.search.open}
        onClick={() => setOpen(true)}
      >
        <Icon glyph={Search} />
      </IconButton>
      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent
          className="top-[15vh] translate-y-0 p-0"
          closeLabel={m.close}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            input.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">{m.search.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {m.search.description}
          </DialogDescription>
          <div className="flex min-h-16 items-center gap-3 border-b border-line px-5 pr-16">
            <Icon className="text-secondary" glyph={Search} />
            <input
              className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={m.search.placeholder}
              ref={input}
              value={query}
            />
          </div>
          <div className="max-h-[22rem] overflow-y-auto p-2">
            {results.length ? (
              results.map((item) => (
                <button
                  className="group flex min-h-14 w-full items-center gap-3 rounded-[var(--radius-md)] px-3 text-left hover:bg-hover focus-visible:bg-hover"
                  key={item.path}
                  onClick={() => choose(item.path)}
                  type="button"
                >
                  <span className="grid size-9 place-items-center rounded-full bg-primary-subtle text-primary">
                    <Icon glyph={item.icon} />
                  </span>
                  <span className="font-medium">{item.title}</span>
                  <Icon className="ml-auto text-primary" glyph={ArrowRight} />
                </button>
              ))
            ) : (
              <div className="px-4 py-10 text-center">
                <p className="font-semibold">{m.search.noResults}</p>
                <p className="mt-1 text-sm text-secondary">
                  {m.search.noResultsDescription}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
